import Vue from 'vue'
import { pick, omit, cloneDeep } from 'lodash-es'
import { uid } from '~/utils/helpers'
import { isURL } from '~/utils/validation'

// https://www.notion.so/eboweb3/8d929c23ed8444ad824e6ddc43d45db6

const dataFrame = (initState) => {
  const state = {
    id: uid(),
    selected: false,
    /** Function that determines if the user can change bg/text colors through the create page */
    styleable: () => true,
    description: () => state.fullName,
    ...initState,
    createSerialize: () =>
      cloneDeep(pick(state, ['id', 'type', 'args', 'position', 'styles'])),
    mintSerialize: () => cloneDeep(pick(state, ['type', 'args', 'position', 'styles'])),
    valid: () => true,
  }

  return state
}
export const createDataFrame = (
  typeMixin,
  args = {},
  position = { x: 0, y: 0 },
  fontColor = '#dedede',
  bgColor = '#000000ff',
  styles = {}
) => {
  return typeMixin(
    dataFrame(
      cloneDeep({
        ...typeMixin, // Add type meta
        position,
        args,
        settings: false,
        styles: {
          backgroundColor: bgColor,
          color: fontColor,
          ...styles,
        },
      })
    )
  )
}

/** WEB */
const webFrame = (state) => {
  state.tags = () => ['m001', 'web', 'website']
  state.description = () => `Web Frame - ${state.args.url}`
  state.fixed = true
  state.settings = () => import('~/components/DataFrames/WebSettings.vue')
  state.valid = () => isURL(state.args.url)
  return state
}
webFrame.type = 'Web'
webFrame.fullName = 'Web Frame'
export const webFrameTemplate =
  (size = {}, url) =>
  () =>
    createDataFrame(
      webFrame,
      // Frames with an arg that contains 'url' are sanitized on the backend
      { url },
      {
        ...size,
        x: 10,
        y: 10,
      }
    )

/** SOCIAL MEDIA */
const twitterProfileFrame = (state) => {
  state.tags = () => ['m021', 'social media', 'twitter', 'profile timeline']
  state.description = () =>
    `Twitter Profile Timeline [${state.args.header ? 'header' : 'w/o header'} ${
      state.args.size === 'sm' ? 'half' : 'full'
    }]`
  state.settings = () => import('~/components/DataFrames/TwitterProfileSettings.vue')
  state.valid = () => !!state.args.account?.trim()
  return state
}
twitterProfileFrame.type = 'TwitterProfile'
twitterProfileFrame.fullName = 'Twitter Profile'
export const twitterProfileFrameTemplate =
  (position, account, { header = true, border = true, size = 'lg' } = {}) =>
  () =>
    createDataFrame(
      twitterProfileFrame,
      {
        account,
        header,
        border,
        size,
      },
      position
    )

export const twitterListFrame = (state) => {
  state.tags = () => ['m022', 'social media', 'twitter', 'list timeline']
  state.description = () =>
    `Twitter List Timeline [${state.args.header ? 'header' : 'w/o header'} ${
      state.args.size === 'sm' ? 'half' : 'full'
    }]`
  state.settings = () => import('~/components/DataFrames/TwitterListSettings.vue')
  state.valid = () => isURL(state.args.listUrl)
  return state
}
twitterListFrame.type = 'TwitterList'
twitterListFrame.fullName = 'Twitter List'
export const twitterListFrameTemplate =
  (position, listUrl, { header = true, border = true, size = 'lg' } = {}) =>
  () =>
    createDataFrame(
      twitterListFrame,
      {
        // Frames with an arg that contains 'url' are sanitized on the backend
        listUrl,
        header,
        border,
        size,
      },
      position
    )

const tweetFrame = (state) => {
  state.tags = () => ['m023', 'social media', 'twitter', 'tweet']
  state.description = () => 'Twitter Tweet Button'
  state.settings = () => import('~/components/DataFrames/TweetSettings.vue')
  return state
}
tweetFrame.type = 'Tweet'
tweetFrame.fullName = 'Twitter Tweet'
export const tweetFrameTemplate =
  (position, size = 'large') =>
  () =>
    createDataFrame(tweetFrame, { size }, position, '#fff', 'rgba(0, 0, 0, 0)')

/** MUSIC */
const spotifyFrame = (state) => {
  state.tags = () => {
    const includes = (type) => state.args.spotifyUrl?.includes(type)
    let frameType
    let frameTag = 'm041'
    if (includes('playlist')) {
      frameType = 'playlist'
    } else if (includes('album')) {
      frameType = 'album'
    } else if (includes('track')) {
      frameType = 'song'
    } else if (includes('show') || includes('episode')) {
      frameType = 'podcast'
      frameTag = 'm042'
    } else if (includes('artist')) {
      frameType = 'artist'
    } else {
      frameType = 'song'
    }

    return [frameTag, 'm041', 'music', 'spotify', frameType]
  }
  state.description = () => 'Spotify Player'
  state.settings = () => import('~/components/DataFrames/SpotifySettings.vue')
  state.valid = () => isURL(state.args.spotifyUrl)
  return state
}
spotifyFrame.type = 'Spotify'
spotifyFrame.fullName = 'Spotify Music'
spotifyFrame.allowMultiple = true
const spotifyPodcastFrame = spotifyFrame.bind({})
spotifyPodcastFrame.type = 'Spotify'
spotifyPodcastFrame.fullName = 'Spotify Podcast'
spotifyPodcastFrame.allowMultiple = true
export const spotifyFrameTemplate = (position, spotifyUrl) => () =>
  createDataFrame(
    spotifyFrame,
    {
      // Frames with an arg that contains 'url' are sanitized on the backend
      spotifyUrl,
    },
    position,
    '#fff',
    'rgba(0, 0, 0, 0)'
  )
export const spotifyPodcastFrameTemplate = (position, spotifyUrl) => () =>
  createDataFrame(
    spotifyPodcastFrame,
    {
      // Frames with an arg that contains 'url' are sanitized on the backend
      spotifyUrl,
    },
    position,
    '#fff',
    'rgba(0, 0, 0, 0)'
  )

/** CRYPTO */
const cryptoFrame = (state) => {
  state.tags = () => {
    const tags = ['m061', 'crypto', 'price', '% change']
    if (state.args.token) tags.push(state.args.token.toLowerCase())
    return tags
  }
  state.description = () => {
    let size = ''
    if (state.args.size === 'lg') size = 'large'
    if (state.args.size === 'md') size = 'medium'
    if (state.args.size === 'sm') size = 'small'

    return `${state.args.token.toUpperCase()} price and % change - ${
      state.args.icon ? '[Icon] ' : ''
    }[${size} ${state.args.useSymbol ? 'symbol' : 'name'}] [%change] [buy link]`
  }
  state.settings = () => import('~/components/DataFrames/CryptoSettings.vue')
  state.valid = () => !!state.args.token?.trim()
  return state
}
cryptoFrame.type = 'Crypto'
cryptoFrame.fullName = 'Crypto Price'
cryptoFrame.allowMultiple = true
export const cryptoFrameTemplate =
  (position, size = 'lg', token = '', { symbol = false, icon = false } = {}) =>
  () =>
    createDataFrame(
      cryptoFrame,
      {
        size,
        token,
        useSymbol: symbol,
        icon,
      },
      position
    )

const tickerFrame = (state) => {
  state.tags = () => [
    'm063',
    ...(state.args.tokens?.map((token) => token.toLowerCase()) || []),
    'crypto',
    'price',
    'price ticker',
    '% change',
  ]
  state.description = () =>
    `Token Ticker ${state.args.speed} speed - [${
      state.args.size === 'lg' ? 'large' : 'small'
    } symbol] [%change] [buy link]`
  state.settings = () => import('~/components/DataFrames/TickerSettings.vue')
  state.valid = () => state.args.tokens.length > 0
  return state
}
tickerFrame.type = 'Ticker'
tickerFrame.fullName = 'Crypto Price Ticker'
tickerFrame.allowMultiple = true
export const tickerFrameTemplate =
  (position, tokens = [], size = 'lg', speed = 'full', icon = false) =>
  () =>
    createDataFrame(
      tickerFrame,
      {
        tokens,
        size,
        speed,
        icon,
      },
      position
    )

const balanceFrame = (state) => {
  state.tags = () => ['m062', 'crypto', 'portfolio', 'balance']
  state.description = () => {
    let size = ''
    if (state.args.size === 'lg') size = 'large'
    if (state.args.size === 'md') size = 'medium'
    if (state.args.size === 'sm') size = 'small'

    return `Portfolio Balance and % change - [${size}] [%change]`
  }
  state.settings = () => import('~/components/DataFrames/BalanceSettings.vue')
  return state
}
balanceFrame.type = 'Balance'
balanceFrame.fullName = 'Crypto Balance'
balanceFrame.allowMultiple = true
export const balanceFrameTemplate =
  (position, size = 'lg') =>
  () =>
    createDataFrame(
      balanceFrame,
      {
        size,
      },
      position
    )

/** SPORTS */
const nflPlayerFrame = (state) => {
  state.tags = () => [
    'm101',
    'sports',
    'football',
    'nfl',
    'player',
    state.args.playerName,
  ]
  state.description = () => 'Sports Player'
  state.settings = () => import('~/components/DataFrames/NFLPlayerSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.playerID?.trim()

  return state
}
nflPlayerFrame.type = 'NFLPlayer'
nflPlayerFrame.fullName = 'NFL Player Stats'
nflPlayerFrame.exclusive = false
nflPlayerFrame.tokenGate = false
export const nflPlayerFrameTemplate = (position, playerID) => () =>
  createDataFrame(
    nflPlayerFrame,
    {
      playerID,
      playerName: '',
      bgAlpha: 0.7,
      // Frames with an arg that contains 'url' are sanitized on the backend
      url: '',
    },
    position
  )

const nflTeamScheduleFrame = (state) => {
  state.tags = () => [
    'm109',
    'sports',
    'football',
    'nfl',
    'team',
    'schedule',
    state.args.teamName,
  ]
  state.description = () => 'NFL Team Schedule'
  state.settings = () => import('~/components/DataFrames/NFLTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
nflTeamScheduleFrame.type = 'NFLTeamSchedule'
nflTeamScheduleFrame.fullName = 'NFL Team Schedule'
nflTeamScheduleFrame.exclusive = true
export const nflTeamScheduleFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    nflTeamScheduleFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const nflTeamStatsFrame = (state) => {
  state.tags = () => [
    'm110',
    'sports',
    'football',
    'nfl',
    'team',
    'stats',
    state.args.teamName,
  ]
  state.description = () => 'NFL Team Stats'
  state.settings = () => import('~/components/DataFrames/NFLTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
nflTeamStatsFrame.type = 'NFLTeamStats'
nflTeamStatsFrame.fullName = 'NFL Team Stats'
export const nflTeamStatsFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    nflTeamStatsFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const nflTeamLeadersFrame = (state) => {
  state.tags = () => [
    'm111',
    'sports',
    'football',
    'nfl',
    'team',
    'leaders',
    state.args.teamName,
  ]
  state.description = () => 'NFL Team Leaders'
  state.settings = () => import('~/components/DataFrames/NFLTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
nflTeamLeadersFrame.type = 'NFLTeamLeaders'
nflTeamLeadersFrame.fullName = 'NFL Team Leaders'
export const nflTeamLeadersFrameTemplate =
  (position, teamID, teamName = '') =>
  () =>
    createDataFrame(
      nflTeamLeadersFrame,
      {
        teamID,
        teamName,
        bgAlpha: 1.0,
      },
      position
    )

const nflTeamOffensiveLeadersFrame = (state) => {
  state.tags = () => [
    'm112',
    'sports',
    'football',
    'nfl',
    'team',
    'offense',
    'leaders',
    state.args.teamName,
  ]
  state.description = () => 'NFL Team Offensive Leaders'
  state.settings = () => import('~/components/DataFrames/NFLTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
nflTeamOffensiveLeadersFrame.type = 'NFLTeamOffensiveLeaders'
nflTeamOffensiveLeadersFrame.fullName = 'NFL Team Offensive Leaders'
export const nflTeamOffensiveLeadersFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    nflTeamOffensiveLeadersFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const nflTeamDefensiveLeadersFrame = (state) => {
  state.tags = () => [
    'm112',
    'sports',
    'football',
    'nfl',
    'team',
    'defense',
    'leaders',
    state.args.teamName,
  ]
  state.description = () => 'NFL Team Defensive Leaders'
  state.settings = () => import('~/components/DataFrames/NFLTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
nflTeamDefensiveLeadersFrame.type = 'NFLTeamDefensiveLeaders'
nflTeamDefensiveLeadersFrame.fullName = 'NFL Team Defensive Leaders'
export const nflTeamDefensiveLeadersFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    nflTeamDefensiveLeadersFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const lightTheme = ['#000', '#fff']
const nflScoresFrame = (state) => {
  state.tags = () => ['m102', 'sports', 'football', 'nfl', 'scores']
  state.description = () => 'Sports Scores'
  return state
}
nflScoresFrame.type = 'NFLScores'
nflScoresFrame.fullName = 'NFL Scores'
export const nflScoresFrameTemplate = (position) => () => {
  return createDataFrame(nflScoresFrame, {}, position, ...lightTheme)
}

const nflTickerFrame = (state) => {
  state.tags = () => ['m103', 'sports', 'football', 'nfl', 'scores', 'scores ticker']
  state.description = () => 'NFL Ticker'
  state.settings = () => import('~/components/DataFrames/NFLTickerSettings.vue')
  return state
}
nflTickerFrame.type = 'NFLTicker'
nflTickerFrame.fullName = 'NFL Scores Ticker'
export const nflTickerFrameTemplate = (position) => () =>
  createDataFrame(nflTickerFrame, { marquee: true }, position, ...lightTheme)

const nflScheduleFrame = (state) => {
  state.tags = () => ['m104', 'sports', 'football', 'nfl', 'schedule']
  state.description = () => 'NFL Schedule'
  return state
}
nflScheduleFrame.type = 'NFLSchedule'
nflScheduleFrame.fullName = 'NFL Schedule'
export const nflScheduleFrameTemplate = (position) => () => {
  return createDataFrame(nflScheduleFrame, {}, position, ...lightTheme)
}

const nflStandingsFrame = (state) => {
  state.tags = () => ['m105', 'sports', 'football', 'nfl', 'standings']
  state.description = () => 'Sports Standings'
  return state
}
nflStandingsFrame.type = 'NFLStandings'
nflStandingsFrame.fullName = 'NFL Standings'
export const nflStandingsFrameTemplate = (position) => () =>
  createDataFrame(nflStandingsFrame, {}, position, ...lightTheme)

const nflLeadersFrame = (state) => {
  state.tags = () => ['m106', 'sports', 'football', 'nfl', 'leaders']
  state.description = () => 'Sports Scores'
  return state
}
nflLeadersFrame.type = 'NFLLeaders'
nflLeadersFrame.fullName = 'NFL Leaders'
export const nflLeadersFrameTempalate = (position) => () =>
  createDataFrame(nflLeadersFrame, {}, position, ...lightTheme)

const nflOffensiveLeadersFrame = (state) => {
  state.tags = () => ['m107', 'sports', 'football', 'nfl', 'offense', 'leaders']
  state.description = () => 'NFL Offensive Leaders'
  return state
}
nflOffensiveLeadersFrame.type = 'NFLOffensiveLeaders'
nflOffensiveLeadersFrame.fullName = 'NFL Offensive Leaders'
export const nflOffensiveLeadersFrameTemplate = (position) => () =>
  createDataFrame(nflOffensiveLeadersFrame, {}, position, ...lightTheme)

const nflDefensiveLeadersFrame = (state) => {
  state.tags = () => ['m108', 'sports', 'football', 'nfl', 'defense', 'leaders']
  state.description = () => 'NFL Defensive Leaders'
  return state
}
nflDefensiveLeadersFrame.type = 'NFLDefensiveLeaders'
nflDefensiveLeadersFrame.fullName = 'NFL Defensive Leaders'
export const nflDefensiveLeadersFrameTemplate = (position) => () =>
  createDataFrame(nflDefensiveLeadersFrame, {}, position, ...lightTheme)

const cfbPlayerFrame = (state) => {
  state.tags = () => [
    'm201',
    'sports',
    'football',
    'cfb',
    'player',
    state.args.playerName,
  ]
  state.description = () => 'CBF Player Stats'
  state.settings = () => import('~/components/DataFrames/CFBPlayerSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.playerID?.trim()

  return state
}
cfbPlayerFrame.type = 'CFBPlayer'
cfbPlayerFrame.fullName = 'CFB Player Stats'
cfbPlayerFrame.exclusive = true
cfbPlayerFrame.tokenGate = true
export const cfbPlayerFrameTemplate = (position, playerID) => () =>
  createDataFrame(
    cfbPlayerFrame,
    {
      playerID,
      playerName: '',
      bgAlpha: 0.7,
      // Frames with an arg that contains 'url' are sanitized on the backend
      url: '',
    },
    position
  )

const cfbScoresFrame = (state) => {
  state.tags = () => ['m202', 'sports', 'football', 'cfb', 'scores']
  state.description = () => 'CFB Scores'
  return state
}
cfbScoresFrame.type = 'CFBScores'
cfbScoresFrame.fullName = 'CFB Scores'
export const cfbScoresFrameTemplate = (position) => () => {
  return createDataFrame(cfbScoresFrame, {}, position, ...lightTheme)
}

const cfbTickerFrame = (state) => {
  state.tags = () => ['m203', 'sports', 'football', 'cfb', 'scores', 'scores ticker']
  state.description = () => 'CFB Ticker'
  state.settings = () => import('~/components/DataFrames/CFBTickerSettings.vue')
  return state
}
cfbTickerFrame.type = 'CFBTicker'
cfbTickerFrame.fullName = 'CFB Scores Ticker'
export const cfbTickerFrameTemplate = (position) => () =>
  createDataFrame(
    cfbTickerFrame,
    { marquee: true, conferenceName: '', conferenceID: null },
    position,
    ...lightTheme
  )
const cfbStandingsFrame = (state) => {
  state.tags = () => ['m205', 'sports', 'football', 'cfb', 'standings']
  state.description = () => 'Sports Standings'
  return state
}
cfbStandingsFrame.type = 'CFBStandings'
cfbStandingsFrame.fullName = 'CFB Standings'
export const cfbStandingsFrameTemplate = (position) => () =>
  createDataFrame(cfbStandingsFrame, {}, position, ...lightTheme)

const cfbTeamStatsFrame = (state) => {
  state.tags = () => [
    'm210',
    'sports',
    'football',
    'cfb',
    'team',
    'stats',
    state.args.teamName,
  ]
  state.description = () => 'CFB Team Stats'
  state.settings = () => import('~/components/DataFrames/CFBTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
cfbTeamStatsFrame.type = 'CFBTeamStats'
cfbTeamStatsFrame.fullName = 'CFB Team Stats'
export const cfbTeamStatsFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    cfbTeamStatsFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const cfbLeadersFrame = (state) => {
  state.tags = () => ['m206', 'sports', 'football', 'cfb', 'leaders']
  state.description = () => 'Sports Scores'
  state.settings = () => import('~/components/DataFrames/CFBConferencesSettings.vue')
  return state
}
cfbLeadersFrame.type = 'CFBLeaders'
cfbLeadersFrame.fullName = 'CFB Leaders'
export const cfbLeadersFrameTempalate = (position, conferenceID, conferenceName) => () =>
  createDataFrame(
    cfbLeadersFrame,
    { conferenceID, conferenceName, bgAlpha: 1.0 },
    position,
    ...lightTheme
  )

const cfbOffensiveLeadersFrame = (state) => {
  state.tags = () => ['m207', 'sports', 'football', 'cfb', 'offense', 'leaders']
  state.description = () => 'CFB Offensive Leaders'
  state.settings = () => import('~/components/DataFrames/CFBConferencesSettings.vue')
  return state
}
cfbOffensiveLeadersFrame.type = 'CFBOffensiveLeaders'
cfbOffensiveLeadersFrame.fullName = 'CFB Offensive Leaders'
export const cfbOffensiveLeadersFrameTemplate =
  (position, conferenceID, conferenceName) => () =>
    createDataFrame(
      cfbOffensiveLeadersFrame,
      { conferenceID, conferenceName, bgAlpha: 1.0 },
      position,
      ...lightTheme
    )

const cfbDefensiveLeadersFrame = (state) => {
  state.tags = () => ['m208', 'sports', 'football', 'cfb', 'defense', 'leaders']
  state.description = () => 'CFB Defensive Leaders'
  state.settings = () => import('~/components/DataFrames/CFBConferencesSettings.vue')
  return state
}
cfbDefensiveLeadersFrame.type = 'CFBDefensiveLeaders'
cfbDefensiveLeadersFrame.fullName = 'CFB Defensive Leaders'
export const cfbDefensiveLeadersFrameTemplate =
  (position, conferenceID, conferenceName) => () =>
    createDataFrame(
      cfbDefensiveLeadersFrame,
      { conferenceID, conferenceName, bgAlpha: 1.0 },
      position,
      ...lightTheme
    )

const cfbTeamScheduleFrame = (state) => {
  state.tags = () => [
    'm209',
    'sports',
    'football',
    'cfb',
    'team',
    'schedule',
    state.args.teamName,
  ]
  state.description = () => 'CFB Team Schedule'
  state.settings = () => import('~/components/DataFrames/CFBTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
cfbTeamScheduleFrame.type = 'CFBTeamSchedule'
cfbTeamScheduleFrame.fullName = 'CFB Team Schedule'
cfbTeamScheduleFrame.exclusive = true
export const cfbTeamScheduleFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    cfbTeamScheduleFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const cfbTeamLeadersFrame = (state) => {
  state.tags = () => [
    'm211',
    'sports',
    'football',
    'cfb',
    'team',
    'leaders',
    state.args.teamName,
  ]
  state.description = () => 'CFB Team Leaders'
  state.settings = () => import('~/components/DataFrames/CFBTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
cfbTeamLeadersFrame.type = 'CFBTeamLeaders'
cfbTeamLeadersFrame.fullName = 'CFB Team Leaders'
export const cfbTeamLeadersFrameTemplate =
  (position, teamID, teamName = '') =>
  () =>
    createDataFrame(
      cfbTeamLeadersFrame,
      {
        teamID,
        teamName,
        bgAlpha: 1.0,
      },
      position
    )

const cfbTeamOffensiveLeadersFrame = (state) => {
  state.tags = () => [
    'm212',
    'sports',
    'football',
    'cfb',
    'team',
    'offense',
    'leaders',
    state.args.teamName,
  ]
  state.description = () => 'CFB Team Offensive Leaders'
  state.settings = () => import('~/components/DataFrames/CFBTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
cfbTeamOffensiveLeadersFrame.type = 'CFBTeamOffensiveLeaders'
cfbTeamOffensiveLeadersFrame.fullName = 'CFB Team Offensive Leaders'
export const cfbTeamOffensiveLeadersFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    cfbTeamOffensiveLeadersFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const cfbTeamDefensiveLeadersFrame = (state) => {
  state.tags = () => [
    'm212',
    'sports',
    'football',
    'cfb',
    'team',
    'defense',
    'leaders',
    state.args.teamName,
  ]
  state.description = () => 'CFB Team Defensive Leaders'
  state.settings = () => import('~/components/DataFrames/CFBTeamSettings.vue')
  state.styleable = () => false
  state.styles = omit(state.styles, ['backgroundColor', 'color'])

  // No need to mint in colors since using team colros
  const baseSerialize = state.mintSerialize
  state.mintSerialize = () => {
    const serialized = baseSerialize()
    delete serialized.styles.backgroundColor
    delete serialized.styles.color
    return serialized
  }
  state.valid = () => state.args.teamID

  return state
}
cfbTeamDefensiveLeadersFrame.type = 'CFBTeamDefensiveLeaders'
cfbTeamDefensiveLeadersFrame.fullName = 'CFB Team Defensive Leaders'
export const cfbTeamDefensiveLeadersFrameTemplate = (position, teamID) => () =>
  createDataFrame(
    cfbTeamDefensiveLeadersFrame,
    {
      teamID,
      teamName: '',
      bgAlpha: 1.0,
    },
    position
  )

const frameRegistry = []
const registerFrame = (mixin, template) => {
  Vue.component(mixin.type, () => import(`~/components/DataFrames/${mixin.type}.vue`))
  frameRegistry.push({
    /** Name of the Vue component for the frame */
    type: mixin.type,
    /** Display name of the frame */
    name: mixin.fullName,
    /** Function that returns a dataFrame instance */
    create: template(),
    /** Function that adds frame-specific methods and properties to frame data */
    mixin,
    /** Whether multiple instances of this data frame type are allowed */
    allowMultiple: mixin.allowMultiple,
    /** Whether this frame can only be present as the the only frame on the screen */
    exclusive: mixin.exclusive,
    /** Whether this frame is token-gated (only those with 500 $EBO can mint) */
    tokenGate: mixin.tokenGate,
  })
}

export function deserializeFrame(data) {
  const frameMeta = frameRegistry.find((frame) => frame.type === data.type)
  if (!frameMeta) {
    throw new Error(`failed to deserialize frame ${data.type}`)
  }

  return frameMeta.mixin(dataFrame(data))
}

registerFrame(webFrame, webFrameTemplate)
registerFrame(twitterProfileFrame, twitterProfileFrameTemplate)
// registerFrame(twitterListFrame, twitterListFrameTemplate)
registerFrame(tweetFrame, tweetFrameTemplate)
registerFrame(spotifyFrame, spotifyFrameTemplate)
registerFrame(spotifyPodcastFrame, spotifyPodcastFrameTemplate)
registerFrame(cryptoFrame, cryptoFrameTemplate)
registerFrame(tickerFrame, tickerFrameTemplate)
registerFrame(balanceFrame, balanceFrameTemplate)
registerFrame(nflPlayerFrame, nflPlayerFrameTemplate)
registerFrame(nflScoresFrame, nflScoresFrameTemplate)
registerFrame(nflTickerFrame, nflTickerFrameTemplate)
registerFrame(nflScheduleFrame, nflScheduleFrameTemplate)
registerFrame(nflStandingsFrame, nflStandingsFrameTemplate)
registerFrame(nflLeadersFrame, nflLeadersFrameTempalate)
registerFrame(nflOffensiveLeadersFrame, nflOffensiveLeadersFrameTemplate)
registerFrame(nflDefensiveLeadersFrame, nflDefensiveLeadersFrameTemplate)
registerFrame(nflTeamScheduleFrame, nflTeamScheduleFrameTemplate)
registerFrame(nflTeamStatsFrame, nflTeamStatsFrameTemplate)
registerFrame(nflTeamLeadersFrame, nflTeamLeadersFrameTemplate)
registerFrame(nflTeamOffensiveLeadersFrame, nflTeamOffensiveLeadersFrameTemplate)
registerFrame(nflTeamDefensiveLeadersFrame, nflTeamDefensiveLeadersFrameTemplate)
// registerFrame(cfbPlayerFrame, cfbPlayerFrameTemplate)
// registerFrame(cfbScoresFrame, cfbScoresFrameTemplate)
// registerFrame(cfbTickerFrame, cfbTickerFrameTemplate)
// registerFrame(cfbStandingsFrame, cfbStandingsFrameTemplate)
// registerFrame(cfbTeamStatsFrame, cfbTeamStatsFrameTemplate)
// registerFrame(cfbLeadersFrame, cfbLeadersFrameTempalate)
// registerFrame(cfbOffensiveLeadersFrame, cfbOffensiveLeadersFrameTemplate)
// registerFrame(cfbDefensiveLeadersFrame, cfbDefensiveLeadersFrameTemplate)
// registerFrame(cfbTeamScheduleFrame, cfbTeamScheduleFrameTemplate)
// registerFrame(cfbTeamLeadersFrame, cfbTeamLeadersFrameTemplate)
// registerFrame(cfbTeamOffensiveLeadersFrame, cfbTeamOffensiveLeadersFrameTemplate)
// registerFrame(cfbTeamDefensiveLeadersFrame, cfbTeamDefensiveLeadersFrameTemplate)

export default frameRegistry
